import React, { useCallback, useState } from 'react';
import {
  Image,
  message,
  Upload,
  Card,
  Row,
  Col,
  Typography,
  Space,
  Divider,
} from 'antd';
import type { UploadProps } from 'antd';
import {
  DeleteOutlined,
  InboxOutlined,
  SaveOutlined,
  CloudUploadOutlined,
} from '@ant-design/icons';
import { AButton } from '../../../components';
import { useToast } from '../../../hooks/toast';
import { sourceImage } from '../../../shared/utils/upload-source';
import { ICompany } from '../../../shared/interfaces';
import DeleteImage from './DeleteImage';
import { putUploadImageCompany } from '../../../http';

const { Dragger } = Upload;
const { Text } = Typography;

interface ICompanyLogos {
  company: ICompany | null;
  setCompany: React.Dispatch<React.SetStateAction<ICompany | null>>;
}

const CompanyLogos: React.FC<ICompanyLogos> = ({ company, setCompany }) => {
  const { addToast } = useToast();
  const [fileLogo, setFileLogo] = useState<Blob | null>(null);
  const [fileHeaderLogo, setHeaderLogo] = useState<Blob | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [modalDelete, setModalDelete] = useState<boolean>(false);
  const [imageLabel, setImageLabel] = useState<string | null>(null);

  const handleUpload = useCallback(async () => {
    const formData = new FormData();
    if (!fileLogo && !fileHeaderLogo) {
      return addToast({
        type: 'warning',
        title: 'Aviso',
        description: `Sem arquivos novos para enviar.`,
      });
    }

    if (fileLogo) formData.append('logo_image', fileLogo);
    if (fileHeaderLogo) formData.append('header_image', fileHeaderLogo);

    setUploading(true);
    try {
      await putUploadImageCompany(formData);
      setFileLogo(null);
      setHeaderLogo(null);
      addToast({
        type: 'success',
        title: 'Sucesso',
        description: `Imagens atualizadas com sucesso.`,
      });
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: `Erro ao realizar upload.`,
      });
    } finally {
      setUploading(false);
    }
  }, [fileLogo, fileHeaderLogo, addToast]);

  const getUploadProps = (
    setFile: React.Dispatch<React.SetStateAction<Blob | null>>,
  ): UploadProps => ({
    name: 'file',
    multiple: false,
    showUploadList: true,
    onRemove: () => setFile(null),
    beforeUpload: (file) => {
      const isImage = ['image/jpeg', 'image/png'].includes(file.type);
      if (!isImage) {
        message.error(`${file.name} não é um arquivo PNG ou JPEG válido.`);
        return Upload.LIST_IGNORE;
      }
      setFile(file);
      return false;
    },
    maxCount: 1,
  });

  const handleDeleteImage = useCallback(
    (label: 'logo_image' | 'header_image') => {
      setImageLabel(label);
      setModalDelete(true);
    },
    [],
  );

  const renderModalDelete = () => {
    if (modalDelete)
      return (
        <DeleteImage
          open={modalDelete}
          close={() => setModalDelete(false)}
          image_label={imageLabel}
          setFileLogo={setFileLogo}
          setHeaderLogo={setHeaderLogo}
          setCompany={setCompany}
        />
      );
  };

  return (
    <div style={{ padding: '20px' }}>
      <Row gutter={[24, 24]}>
        <Col xs={24} md={8}>
          <Card
            title="Logomarca"
            bordered={false}
            className="shadow-sm"
            style={{ height: '100%' }}
            extra={<Text type="secondary">100 x 100 px</Text>}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 16,
              }}
            >
              {company?.logo_image ? (
                <div style={{ textAlign: 'center' }}>
                  <div
                    style={{
                      padding: 8,
                      border: '1px solid #f0f0f0',
                      borderRadius: 8,
                      marginBottom: 16,
                    }}
                  >
                    <Image
                      width={150}
                      height={150}
                      style={{ objectFit: 'contain' }}
                      src={`${sourceImage}${company?.logo_image}`}
                      fallback="error"
                    />
                  </div>
                  <AButton
                    type="primary"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteImage('logo_image')}
                  >
                    Remover Logo
                  </AButton>
                </div>
              ) : (
                <Dragger
                  {...getUploadProps(setFileLogo)}
                  style={{ width: '100%' }}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">Clique ou arraste a logo</p>
                  <p className="ant-upload-hint">Suporta apenas PNG ou JPG</p>
                </Dragger>
              )}
            </div>
          </Card>
        </Col>

        <Col xs={24} md={16}>
          <Card
            title="Cabeçalho (Header)"
            bordered={false}
            className="shadow-sm"
            style={{ height: '100%' }}
            extra={<Text type="secondary">1300 x 250 px</Text>}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 16,
              }}
            >
              {company?.header_image ? (
                <div style={{ width: '100%', textAlign: 'center' }}>
                  <div
                    style={{
                      padding: 8,
                      border: '1px solid #f0f0f0',
                      borderRadius: 8,
                      marginBottom: 16,
                      background: '#fafafa',
                    }}
                  >
                    <Image
                      width="100%"
                      height={200}
                      style={{ objectFit: 'cover', borderRadius: 4 }}
                      src={`${sourceImage}${company?.header_image}`}
                      fallback="error"
                    />
                  </div>
                  <AButton
                    type="primary"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteImage('header_image')}
                  >
                    Remover Cabeçalho
                  </AButton>
                </div>
              ) : (
                <Dragger
                  {...getUploadProps(setHeaderLogo)}
                  style={{ width: '100%' }}
                >
                  <p className="ant-upload-drag-icon">
                    <CloudUploadOutlined />
                  </p>
                  <p className="ant-upload-text">Clique ou arraste o banner</p>
                  <p className="ant-upload-hint">
                    Tamanho recomendado: 1300x250
                  </p>
                </Dragger>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {(fileLogo || fileHeaderLogo) && (
        <>
          <Divider />
          <Row justify="end">
            <Col>
              <Space>
                <AButton onClick={() => window.location.reload()}>
                  Cancelar
                </AButton>
                <AButton
                  type="primary"
                  onClick={handleUpload}
                  loading={uploading}
                  icon={<SaveOutlined />}
                  size="large"
                >
                  Salvar Alterações
                </AButton>
              </Space>
            </Col>
          </Row>
        </>
      )}

      {renderModalDelete()}
    </div>
  );
};

export default CompanyLogos;
